import React, { useState } from "react";
import { CheckCircle2, MapPin, Mail, Send, Building2, Users, Info } from "lucide-react";
import { chiSiamo, contatti, sedi } from "../data/pages";

const rich = (parts) =>
  parts.map((p, i) =>
    p.b ? (
      <strong key={i} className="font-semibold text-[#16182C]">
        {p.t}
      </strong>
    ) : (
      <span key={i}>{p.t}</span>
    )
  );

/* ---------------- Chi Siamo ---------------- */
const ChiSiamo = () => (
  <div className="max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-20">
    <div className="flex items-center gap-4 mb-6">
      <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#F6E8E8] text-[#E23028] shrink-0">
        <Info size={28} />
      </div>
      <h1 className="heading-navy font-bold text-3xl md:text-4xl tracking-tight">{chiSiamo.title}</h1>
    </div>

    <div className="space-y-5 text-[#5a5a66] text-[16px] leading-relaxed">
      {chiSiamo.paragraphs.map((p, i) => (
        <p key={i}>{rich(p)}</p>
      ))}
    </div>

    <h2 className="heading-navy font-bold text-2xl mt-12 mb-6">{chiSiamo.whyTitle}</h2>
    <div className="grid sm:grid-cols-2 gap-5">
      {chiSiamo.reasons.map((r, i) => (
        <div key={i} className="bg-[#F9F5F5] rounded-xl p-5 flex gap-3">
          <CheckCircle2 className="text-[#E23028] mt-0.5 shrink-0" size={22} />
          <div>
            <p className="font-semibold text-[#16182C]">{r.h}</p>
            <p className="text-[#7a7a85] text-[15px] mt-1 leading-relaxed">{r.t}</p>
          </div>
        </div>
      ))}
    </div>

    <p className="mt-10 text-[#5a5a66] text-[16px] leading-relaxed">{chiSiamo.closing}</p>

    <div className="mt-10 overflow-hidden rounded-xl shadow-lg">
      <img src={chiSiamo.image} alt="Team SpedyFly" className="w-full h-[300px] md:h-[420px] object-cover" />
    </div>
  </div>
);

/* ---------------- Contatti ---------------- */
const ContactForm = () => {
  const [form, setForm] = useState({ nome: "", telefono: "", email: "", descrizione: "" });
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.nome || !form.email) return;
    setSent(true);
    setForm({ nome: "", telefono: "", email: "", descrizione: "" });
    setTimeout(() => setSent(false), 5000);
  };

  const inputCls =
    "w-full rounded-md border border-gray-300 px-4 py-3 text-[#16182C] outline-none focus:border-[#E23028] transition-colors";

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8">
      <h2 className="heading-navy font-bold text-3xl mb-6 text-center">Contattaci</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-sm font-medium text-[#4a4a55]">Nome</span>
          <input value={form.nome} onChange={set("nome")} className={`mt-1 ${inputCls}`} required />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-[#4a4a55]">Telefono</span>
          <input value={form.telefono} onChange={set("telefono")} className={`mt-1 ${inputCls}`} />
        </label>
      </div>
      <label className="block mt-4">
        <span className="text-sm font-medium text-[#4a4a55]">Email</span>
        <input type="email" value={form.email} onChange={set("email")} className={`mt-1 ${inputCls}`} required />
      </label>
      <label className="block mt-4">
        <span className="text-sm font-medium text-[#4a4a55]">Descrizione</span>
        <textarea value={form.descrizione} onChange={set("descrizione")} rows={5} className={`mt-1 ${inputCls} resize-none`} />
      </label>
      <button
        type="submit"
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#E23028] hover:bg-[#c31f18] text-white font-semibold py-3 rounded-md transition-colors"
      >
        <Send size={18} /> Invia
      </button>
      {sent && (
        <p className="mt-4 text-center text-[#2e7d32] bg-[#e8f5e9] border border-[#c8e6c9] rounded-md py-2 text-sm">
          Messaggio inviato! Ti risponderemo al pi\u00f9 presto.
        </p>
      )}
    </form>
  );
};

const Contatti = () => (
  <div className="max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-20">
    <div className="overflow-hidden rounded-xl shadow-lg mb-10">
      <img src={contatti.image} alt="Servizio clienti SpedyFly" className="w-full h-[280px] md:h-[380px] object-cover" />
    </div>

    <h1 className="heading-navy font-bold text-2xl md:text-3xl mb-4">{contatti.title}</h1>
    <p className="text-[#5a5a66] text-[16px] leading-relaxed">{contatti.intro}</p>

    <h2 className="heading-navy font-bold text-xl mt-10 mb-5">{contatti.subTitle}</h2>
    <div className="space-y-4">
      {contatti.items.map((it, i) => (
        <div key={i} className="flex gap-3">
          <CheckCircle2 className="text-[#E23028] mt-0.5 shrink-0" size={20} />
          <p className="text-[#5a5a66] text-[15px] leading-relaxed">
            <strong className="text-[#16182C] font-semibold">{it.h}: </strong>
            {it.t}
          </p>
        </div>
      ))}
    </div>

    <a
      href={`mailto:${contatti.email}`}
      className="mt-8 inline-flex items-center gap-2 text-[#E23028] font-semibold hover:underline"
    >
      <Mail size={18} /> {contatti.email}
    </a>

    <div className="mt-12">
      <ContactForm />
    </div>
  </div>
);

/* ---------------- Le Nostre Sedi ---------------- */
const Sedi = () => (
  <div className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-20">
    <div className="flex items-center gap-4 mb-6">
      <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#F6E8E8] text-[#E23028] shrink-0">
        <Building2 size={28} />
      </div>
      <h1 className="heading-navy font-bold text-3xl md:text-4xl tracking-tight">{sedi.title}</h1>
    </div>
    <p className="text-[#5a5a66] text-[16px] leading-relaxed max-w-4xl">{sedi.intro}</p>

    {/* Providers */}
    <h2 className="heading-navy font-bold text-2xl mt-12 mb-4 flex items-center gap-2">
      <Users size={22} className="text-[#E23028]" /> {sedi.providersTitle}
    </h2>
    <p className="text-[#5a5a66] text-[15px] leading-relaxed max-w-4xl">{sedi.providersIntro}</p>
    <div className="mt-6 flex flex-wrap gap-3">
      {sedi.providers.map((p) => (
        <span
          key={p}
          className="px-4 py-2 rounded-full bg-[#F9F5F5] border border-[#eadada] text-[#16182C] text-sm font-semibold"
        >
          {p}
        </span>
      ))}
    </div>

    {/* Offices */}
    <h2 className="heading-navy font-bold text-2xl mt-14 mb-4">{sedi.officesTitle}</h2>
    <p className="text-[#5a5a66] text-[15px] leading-relaxed max-w-4xl">{sedi.officesIntro}</p>

    <div className="mt-8 space-y-10">
      {sedi.regions.map((region) => (
        <div key={region.name}>
          <h3 className="text-lg font-bold text-white bg-[#12121C] inline-block px-5 py-2 rounded-md mb-5">
            {region.name}
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {region.offices.map((o, i) => (
              <div
                key={i}
                className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow flex items-start gap-3"
              >
                <MapPin className="text-[#E23028] mt-0.5 shrink-0" size={18} />
                <div>
                  <p className="font-semibold text-[#16182C] text-sm">{o.p}</p>
                  <p className="text-[#7a7a85] text-[13px] mt-0.5 leading-snug">{o.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>

    <p className="mt-12 text-[#5a5a66] text-[16px] leading-relaxed max-w-4xl">{sedi.closing}</p>
  </div>
);

const InfoPage = ({ page }) => {
  return (
    <section className="bg-white min-h-[60vh]">
      {page === "chi-siamo" && <ChiSiamo />}
      {page === "contatti" && <Contatti />}
      {page === "sedi" && <Sedi />}
    </section>
  );
};

export default InfoPage;
